import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { createButton, createButtonEdit, createButtonSave, createButtonView } from "../../../../utils/button";
import { checkAttribute, initUseForm, onClickEdit, onClickView, watchRulesChildren } from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageExportCsv,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { required, requiredWhen, validateError } from "../../../../utils/validate";
import { callHazMgtStatus, callHazMgtType } from "../sell/sell.section";
import { createConditionAction, createConditionForm } from "./buy.condition";
import { callGetHazLicenseReserveAndUse, createColumns, createField } from "./buy.section";
import { openModalForm as openModalFormConfirmBuy } from "./modal/confirm-buy.logic";
import { callGetStorageLocationSubReserveAndUse } from "./storage-location/storage-location.logic";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();
export const actionPrefix = "resource/v1/transaction/hazMgt";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const onSearch = ({ props, page, state }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const fromCompanyNameThIndex = customWhere.findIndex(d => d.name === "refFrom.companyNameTh");
    if (fromCompanyNameThIndex !== -1) {
        const fromCompanyNameThWhere = customWhere[fromCompanyNameThIndex];
        customWhere.splice(
            fromCompanyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refFrom.companyNameTh",
                type: "String",
                operator: "like",
                value: fromCompanyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refFrom.fullNameTh",
                type: "String",
                operator: "like",
                value: fromCompanyNameThWhere.value
            }
        );
    }
    const toCompanyNameThIndex = customWhere.findIndex(d => d.name === "refTo.companyNameTh");
    if (toCompanyNameThIndex !== -1) {
        const toCompanyNameThWhere = customWhere[toCompanyNameThIndex];
        customWhere.splice(
            toCompanyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refTo.companyNameTh",
                type: "String",
                operator: "like",
                value: toCompanyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refTo.fullNameTh",
                type: "String",
                operator: "like",
                value: toCompanyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "toOther",
                type: "String",
                operator: "like",
                value: toCompanyNameThWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const createWhereFor = ({ props }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    whereGroup.push([{ name: "refTo.id", type: "UUID", operator: "=", value: userInfo.id }]);
    if (userInfo.refParent?.id) {
        whereGroup.push([{ name: "refTo.id", type: "UUID", operator: "=", value: userInfo.refParent?.id }]);
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ constant: true, name: "1", type: "String", operator: "=", value: "1" }]);
    }
    return [
        { name: "refType.key", type: "String", operator: "=", value: "1" },
        { name: "refStatus.key", type: "String", operator: "!=", value: "draft" },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const createSearchOneCriteria = ({ props, id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**", "refFrom.refPersonType.*"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }, ...createWhereFor({ props })],
            children: [
                {
                    name: "statuses",
                    asName: "lastStatus.forFromRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["draft", "reEdit"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forToRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["wait", "approve", "notApprove"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "attachments",
                    select: ["*"],
                    page: 0,
                    size: 1
                },
                {
                    name: "storageLocations",
                    select: [
                        "**",
                        "refStorageLocationSubItem.refInformProductQty.id",
                        "refStorageLocationSubItem.refInformProductQty.version",
                        "refStorageLocationSubItem.refInformProductQty.refInformProduct.id",
                        "refStorageLocationSubItem.refInformProductQty.refInformProduct.version",
                        "refStorageLocationSubItem.refInformProductQty.refInformProduct.refInform.id",
                        "refStorageLocationSubItem.refInformProductQty.refInformProduct.refInform.version",
                        "refStorageLocationSubItem.refInformProductQty.refInformProduct.refInform.importExportDate",
                        "refStorageLocationSubItem.refInformProductQty.refInformProduct.refInform.approveCode",
                        "refHazLicenseTo.refOperation.id",
                        "refHazLicenseTo.refOperation.version",
                        "refHazLicenseTo.refOperation.key",
                        "refHazLicenseTo.refOperation.value",
                        "refHazLicenseTo.refHazLicenseObjective.id",
                        "refHazLicenseTo.refHazLicenseObjective.version",
                        "refHazLicenseTo.refHazLicenseObjective.key",
                        "refHazLicenseTo.refHazLicenseObjective.value",
                        "refStorageLocation.refUserCompany.*",
                        "refHaz.refHazObject.*",
                    ],
                    page: 0,
                    size: 1,
                    children: [
                        {
                            name: "stores",
                            select: ["*", "refStorageLocation.*", "refStorageLocationSub.*", "refStorageLocationSubItem.*"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "createDate", type: "ASC" }]
                        },
                        {
                            name: "refStorageLocation.items",
                            select: ["*", "refStorageLocationSub.*"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "refStorageLocationSub.buildingName", type: "ASC" }]
                        },
                        {
                            name: "refHazLicenseTo.hazLicenseCountries",
                            select: ["*", "refCountry.*"],
                            page: 0,
                            size: 1000000
                        }
                    ]
                }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const onClickSave = ({ props, data, refs, state, formParams, successText }) => {
    const { viewOnly, routerPrefix } = props;
    return validate({ props, data, refs }).then(() => {
        setIsLoadingApp(true);
        return callSave({ form: { ...data.useForm.modelRef, ...formParams } })
            .finally(() => setIsLoadingApp(false))
            .then(record => {
                if (successText) notification["success"]({ message: successText });
                if (viewOnly) initForm({ props, data, refs, state });
                else onClickView({ record, routerPrefix });
            });
    });
};

export const validate = ({ props, data, refs }) => {
    const { menuContext } = props;
    const rulesTop = createRulesTop({ props, data, refs });
    let tabKey = null;
    return data.useForm.validate(Object.keys(rulesTop)).catch(error => {
        if (tabKey) {
            menuContext.tabKey = tabKey;
        }
        validateError({ error });
        throw error;
    });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id
        ? `${modelRef.refTo?.companyNameTh || modelRef.refTo?.fullNameTh} ${modelRef.storageLocations[0].refHaz.approveCode} (${
              modelRef.refStatus?.value
          })`
        : i18n.global.t("button.create");
};

export const createForm = () => {
    return {
        refType: null,
        refFrom: null,
        refTo: null,
        toOther: null,
        refHaz: null,
        refStorageLocation: null,
        refStorageLocationSub: null,
        refInform: null,
        amount: null,
        operationDatetime: null,
        refHazLicenseFrom: null,
        refHazLicenseTo: null,
        refPackage: null,
        refStatus: null,
        attachments: [],
        storageLocations: [
            { refHaz: null, refStorageLocation: null, refStorageLocationSub: null, refStorageLocationSubItem: null, amount: null, stores: [] }
        ],
        lastStatus: {
            forFromRemark: [{ remark: null, statusDatetime: null }],
            forToRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ props, data, refs, key }) => {
    return {
        ...createRulesTop({ props, data, refs, key })
    };
};

export const createRulesTop = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const initialModel = data.useForm?.initialModel || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const initModel = key ? lodash.get(initialModel, key) : initialModel;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}storageLocations.0.refHazLicenseTo`]: [requiredWhen({ when: () => data.sendApprove })],
        [`${prefix}storageLocations.0.stores`]: [requiredWhen({ when: () => data.sendApprove })]
    };
    model?.storageLocations?.[0]?.stores?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}storageLocations.0.stores.${index}.refStorageLocation`] = [required];
            rules[`${prefix}storageLocations.0.stores.${index}.refStorageLocationSub`] = [required];
            rules[`${prefix}storageLocations.0.stores.${index}.amount`] = [
                {
                    validator: (rule, value) => {
                        if (Array.isArray(value)) {
                            if (value.filter(d => d.isDelete !== true).length === 0) {
                                return Promise.reject(i18n.global.t("error.form.required"));
                            }
                        } else {
                            if (value === undefined || value === null || value?.length === 0 || value?.toString()?.length === 0) {
                                return Promise.reject(i18n.global.t("error.form.required"));
                            }
                        }
                        const amountOld = initModel.storageLocations?.[0]?.stores
                            .filter(d => d.refStorageLocationSub?.id === item.refStorageLocationSub?.id)
                            .map(d => d.amount)
                            .reduce((accumulator, current) => parseFloat(accumulator || 0) + parseFloat(current || 0), 0);
                        const balance =
                            item.refStorageLocationSub?.useKg -
                                item.refStorageLocationSub?.useKgReserve -
                                item.refStorageLocationSub?.useKgUse +
                                amountOld || 0;
                        if (parseFloat(value) > balance) {
                            return Promise.reject(i18n.global.t("error.form.maxValue", { max: balance }));
                        }
                        const sellAmount = model.storageLocations?.[0]?.amount;
                        const totalStore = model.storageLocations?.[0]?.stores
                            .filter(d => d.isDelete !== true)
                            .map(d => d.amount)
                            .reduce((accumulator, current) => parseFloat(accumulator || 0) + parseFloat(current || 0), 0);
                        if (totalStore !== sellAmount) {
                            return Promise.reject(i18n.global.t("views.sellBuy.error.store.invalid", { amount: sellAmount }));
                        }
                        const hazLicense = model.storageLocations?.[0]?.refHazLicenseTo;
                        const quantityRemaining = hazLicense
                            ? parseFloat(hazLicense.quantity || 0) -
                              parseFloat(hazLicense.quantityReserve || 0) -
                              parseFloat(hazLicense.quantityUse || 0)
                            : 0;
                        if (totalStore > quantityRemaining) {
                            return Promise.reject(
                                i18n.global.t("error.v1InformProductQtyService.processNetWeightLicense.quantityRemaining.notEnough")
                            );
                        }
                        return Promise.resolve();
                    },
                    required: true,
                    trigger: "change"
                }
            ];
        }
    });
    return rules;
};

export const loadDefaultForm = ({ props, data }) => {
    const { form, forceDefault } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [callHazMgtStatus({}), callHazMgtType({})];
    return Promise.all(promises).then(arr => {
        data.statuses = arr[0];
        data.hazMgtTypes = arr[1];
        const userInfo = store.state.resource.auth.info?.user;

        if ((!id && !form) || forceDefault) {
            modelRef.refFrom = userInfo;
            modelRef.refType = data.hazMgtTypes.find(d => d.key === "1");
        }
    });
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, id }) }).then(page => {
            return Promise.all([
                callGetStorageLocationSubReserveAndUse({
                    stroageLocationSubId: lodash.uniq(
                        page.data
                            .flatMap(hazMgt =>
                                hazMgt.storageLocations.flatMap(storageLocation =>
                                    storageLocation.stores.map(store => store.refStorageLocationSub?.id)
                                )
                            )
                            .filter(d => d)
                    )
                }),
                callGetHazLicenseReserveAndUse({
                    hazLicenseId: lodash.uniq(
                        page.data
                            .flatMap(hazMgt => hazMgt.storageLocations.map(storageLocation => storageLocation.refHazLicenseTo?.id))
                            .filter(d => d)
                    )
                })
            ])
                .then(arr => {
                    const storageLocationSubReserveUses = arr[0];
                    const hazLicenseReserveUses = arr[1];
                    page.data.forEach(hazMgt => {
                        hazMgt.storageLocations.forEach(storageLocation => {
                            storageLocation.stores.forEach(store => {
                                if (store.refStorageLocationSub) {
                                    const reserveUse = storageLocationSubReserveUses.find(
                                        reserveUse => reserveUse.refStorageLocationSubId === store.refStorageLocationSub.id
                                    );
                                    store.refStorageLocationSub.useKgReserve = reserveUse.reserve;
                                    store.refStorageLocationSub.useKgUse = reserveUse.use;
                                }
                            });

                            if (storageLocation.refHazLicenseTo) {
                                const reserveUse = hazLicenseReserveUses.find(
                                    reserveUse => reserveUse.refHazLicenseId === storageLocation.refHazLicenseTo.id
                                );
                                storageLocation.refHazLicenseTo.quantityReserve = reserveUse.reserve;
                                storageLocation.refHazLicenseTo.quantityUse = reserveUse.use;
                            }
                        });
                    });
                })
                .then(() => page);
        });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const createAppPageHeaderDefault = ({ props, data, refs, state }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    /*
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    */
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    data.sendApprove = false;
                    let statusRemark = null;
                    if ([undefined, "draft", "reEdit"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forFromRemark[0].remark;
                    } else if (["wait"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forToRemark[0].remark;
                    }
                    onClickSave({
                        props,
                        data,
                        refs,
                        state,
                        formParams: { statusRemark }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (id && isActive && canApprove) {
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.action")}`,
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    data.sendApprove = false;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmBuy({
                            onOk: record => {
                                const isStatusBack = ["reEdit"].includes(record.refStatus?.key);
                                data.sendApprove = ["approve"].includes(record.refStatus?.key);
                                let promise = Promise.resolve();
                                if (data.sendApprove) {
                                    promise = validate({ props, data, refs });
                                }
                                promise.then(() => {
                                    onClickSave({
                                        props,
                                        data,
                                        refs,
                                        state,
                                        formParams: {
                                            statusRemark: isStatusBack
                                                ? modelRef.lastStatus.forFromRemark[0].remark
                                                : modelRef.lastStatus.forToRemark[0].remark,
                                            statusRemarkBefore: isStatusBack ? modelRef.lastStatus.forToRemark[0].remark : null,
                                            refStatus: record.refStatus
                                        },
                                        successText: i18n.global.t(`views.sellBuy.modal.${record.refStatus?.key}`)
                                    });
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    /*
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    */
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const initForm = ({ props, data, refs, state }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.storageLocations?.[0]?.stores.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTop({ props, data, refs }),
        itemsKey: "storageLocations.0.stores"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs, state })
                }
            });
        }
    });
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix } = props;
    const stateKey = `null`;
    if (stateGroup[stateKey]) {
        return stateGroup[stateKey];
    }
    const state = {
        columns: createColumns({ props }),
        page: createPage({
            options: {
                isDistinct: true,
                select: ["*", "refFrom.*", "refFrom.refPersonType.*", "refTo.*", "refTo.refPersonType.*", "refStatus.*"],
                sort: [{ name: "updateDate", type: "DESC" }],
                children: [
                    {
                        name: "statuses",
                        asName: "firstStatus.wait",
                        select: ["**"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refStatus.key", type: "String", operator: "=", value: "wait" }],
                        sort: [{ name: "createDate", type: "ASC" }]
                    },
                    {
                        name: "storageLocations",
                        select: [
                            "*",
                            "refHaz.*",
                            "refStorageLocation.*",
                            "refStorageLocationSub.*",
                            "refStorageLocationSubItem.*",
                            "refStorageLocationSubItem.refHazLicense.*",
                            "refStorageLocationSubItem.refHazLicense.refOperation.*"
                        ],
                        page: 0,
                        size: 1
                    }
                ]
            }
        }),
        tableOptions: { search: { activeKey: "Search" } },
        conditionAction: createConditionAction()
    };
    state.manages = [
        createManageSelect({ menuContext }),
        /*
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canArchive({ props, record }) })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        */
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [
        /* createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) }) */
    ];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        })
    ];
    return (stateGroup[stateKey] = state);
};

export const stateGroup = [];
